import { Controller } from "@hotwired/stimulus"

/**
 * Handles hover-to-expand functionality for sidebar accordion menus on desktop.
 * Falls back to click behavior on mobile/tablet devices.
 * Animation is handled via CSS for better performance.
 *
 * Usage:
 *   <div data-controller="sidebar-menu-hover">
 *     <div data-sidebar-menu-hover-target="item"
 *          data-action="mouseenter->sidebar-menu-hover#enter mouseleave->sidebar-menu-hover#leave">
 *       ...
 *     </div>
 *   </div>
 */
export default class extends Controller {
  static targets = ["item"]
  static values = {
    enterDelay: { type: Number, default: 750 },
    exitDelay: { type: Number, default: 750 }
  }

  declare readonly itemTargets: HTMLElement[]
  declare readonly enterDelayValue: number
  declare readonly exitDelayValue: number

  private hoverTimeout: ReturnType<typeof setTimeout> | null = null
  private enterTimeout: ReturnType<typeof setTimeout> | null = null
  private breakpoint: number = 992
  private boundHandleClick: EventListener | null = null

  connect() {
    const cssBreakpoint = getComputedStyle(document.documentElement)
      .getPropertyValue("--bs-lg")
      .trim()
    if (cssBreakpoint) {
      this.breakpoint = parseInt(cssBreakpoint, 10)
    }

    this.boundHandleClick = this.handleClick.bind(this) as EventListener
    this.element.addEventListener("click", this.boundHandleClick)
  }

  disconnect() {
    if (this.hoverTimeout) {
      clearTimeout(this.hoverTimeout)
    }
    if (this.enterTimeout) {
      clearTimeout(this.enterTimeout)
    }
    if (this.boundHandleClick) {
      this.element.removeEventListener("click", this.boundHandleClick)
    }
  }

  private handleClick(event: Event) {
    if (!this.isDesktop()) return

    const target = event.target as HTMLElement | null
    if (!target) return
    const clickedLink = target.closest(".menu-link")
    if (!clickedLink) return

    // Check if click is inside an accordion submenu
    const clickedAccordion = target.closest("[data-sidebar-menu-hover-target='item']")

    // Close all accordion menus not containing the clicked element
    this.itemTargets.forEach((item) => {
      if (item !== clickedAccordion && !item.classList.contains("here")) {
        item.classList.remove("hover", "show")
      }
    })
  }

  enter(event: MouseEvent) {
    if (!this.isDesktop()) return

    if (this.hoverTimeout) {
      clearTimeout(this.hoverTimeout)
      this.hoverTimeout = null
    }

    const item = event.currentTarget as HTMLElement

    this.enterTimeout = setTimeout(() => {
      item.classList.add("hover", "show")
    }, this.enterDelayValue)
  }

  leave(event: MouseEvent) {
    if (!this.isDesktop()) return

    if (this.enterTimeout) {
      clearTimeout(this.enterTimeout)
      this.enterTimeout = null
    }

    const item = event.currentTarget as HTMLElement

    // Don't collapse if this menu contains the active page
    if (item.classList.contains("here")) return

    this.hoverTimeout = setTimeout(() => {
      item.classList.remove("hover", "show")
    }, this.exitDelayValue)
  }

  private isDesktop(): boolean {
    return window.innerWidth >= this.breakpoint
  }
}
